import { Fragment } from "react";
import { ComboboxOptions } from "@headlessui/react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";

import { SelectOption } from "@/types/SelectTypes.type";
import Spinner from "@/components/atoms/Spinner";
import ComboBoxCustomOption from "@/components/molecules/combobox/ComboBoxCustomOption";

type ComboBoxOptionsProps<T extends object = {}> = {
  query: string;
  filteredOptions: SelectOption<T>[];
  isLoading: boolean;
  renderOption?: (option: SelectOption<T>) => React.ReactNode;
  displayValue: (option: SelectOption<T>) => string;
  optionsClassName?: string;
  emptyNode?: React.ReactNode;
  allowFreeText?: boolean;
  showOptionsOnFocus?: boolean;
};

const ComboBoxOptions = <T extends object = {}>({
  query,
  filteredOptions,
  isLoading,
  renderOption,
  displayValue,
  optionsClassName,
  emptyNode,
  allowFreeText = true,
  showOptionsOnFocus,
}: ComboBoxOptionsProps<T>) => {
  return (
    (query || showOptionsOnFocus) && (
      <ComboboxOptions
        data-testid="combobox-options"
        className={clsx(
          "absolute z-10 mt-1 max-h-60 overflow-auto rounded bg-white py-1 text-sm shadow-md focus:outline-none",
          optionsClassName ?? "w-full",
        )}
      >
        {allowFreeText && query && (
          <ComboBoxCustomOption option={{ id: `text-${query}`, name: query, isCustom: true }}>
            <span className={"block truncate"}>{query}</span>
          </ComboBoxCustomOption>
        )}

        {isLoading ? (
          <IsLoading />
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            if (option.isSelectable === false) {
              return (
                <Fragment key={option.id}>
                  <div className="px-3 py-2 text-sm font-medium text-quaternary">{option.name}</div>

                  {option.children?.map((child) => (
                    <ComboBoxCustomOption key={child.id} option={child}>
                      <div className="max-w-full truncate">
                        {renderOption ? renderOption(child) : displayValue(child)}
                      </div>
                    </ComboBoxCustomOption>
                  ))}
                </Fragment>
              );
            }

            return (
              <ComboBoxCustomOption key={option.id} option={option}>
                <div className={"max-w-full truncate"}>
                  {renderOption ? renderOption(option) : displayValue(option)}
                </div>
              </ComboBoxCustomOption>
            );
          })
        ) : emptyNode ? (
          emptyNode
        ) : (
          <NoResults />
        )}
      </ComboboxOptions>
    )
  );
};

const NoResults = () => {
  const { t } = useTranslation();

  return (
    <div className={"flex flex-col items-center justify-center px-3 py-4 text-center text-sm text-quaternary"}>
      <HiOutlineDocumentMagnifyingGlass className={"mb-2 size-8"} />
      <p>{t("noResults.found")}</p>
      <p>{t("noResults.try")}</p>
    </div>
  );
};

const IsLoading = () => {
  const { t } = useTranslation();
  return (
    <div className={"flex items-center gap-2 px-3 py-4 text-sm text-quaternary"}>
      <Spinner />
      <p>{t("searching")}</p>
    </div>
  );
};

export default ComboBoxOptions;
